import { type ComponentPropsWithoutRef, type Ref, useState } from "react"
import { useOutsideClick } from "../../../../hooks/useOutsideClick"
import { classNames } from "../../../messaging/utils"
import { Tooltip } from "../../atoms/Tooltip"
import styles from "./ClickableTooltip.module.scss"
import type { DivotTooltipProps } from "../DivotTooltip/DivotTooltip"

export enum Placement {
    none, // Caller will use the default or specify their own placement
    top,
    // TODO: Add more as needed
}

const TOOLTIP_EXTRA_PROPS = {
    "data-testid": "popup-tooltip",
}

interface TooltipProps extends ComponentPropsWithoutRef<"div"> {
    /**
     * Text to display in the tooltip
     */
    text?: string
    /**
     * Additional classes to provide to the inner div
     */
    className?: string
    /**
     * Additional classes to provide to the inner div
     */
    toolTipClassName?: string
    /**
     * Props to pass to the divot tooltip component
     */
    divotProps?: DivotTooltipProps
    /**
     * Props to pass to the divot tooltip component
     */
    innerRef?: Ref<HTMLDivElement>
    /**
     * General cardinal placement of the tooltip
     */
    placement?: Placement
}
/**
 * A text tooltip that pops up when clicked and closes when clicked anywhere.
 * If `text` is falsey, the tooltip will not render.
 *
 * @example
 * ```jsx
 * const ComponentWithTooltip = () => {
 *   return (
        <ClickableTooltip
            text={t`Tooltip Text.`}
            placement={Placement.top}
        >
            <div>Hello World</div>
        </ClickableTooltip>
 *   )
 * }
 * ```
 */
export const ClickableTooltip = ({
    text,
    children,
    className,
    toolTipClassName,
    divotProps,
    innerRef,
    placement = Placement.none,
}: TooltipProps) => {
    const [isTooltipOpen, setIsTooltipOpen] = useState(false)
    const openToolTip = () => {
        setIsTooltipOpen(!isTooltipOpen)
    }
    const closeToolTip = () => {
        setIsTooltipOpen(false)
    }
    const inputRef = useOutsideClick(closeToolTip)

    if (!text) {
        return children
    }

    let placementStyle
    switch (placement) {
        case Placement.top:
            placementStyle = styles.top
            break
        default:
            break
    }

    return (
        <div
            className={classNames(styles.tooltipContainer, className)}
            onClick={openToolTip}
        >
            <div ref={inputRef}>{children}</div>
            {isTooltipOpen && (
                <Tooltip
                    divotProps={divotProps}
                    innerRef={innerRef}
                    className={classNames(placementStyle, toolTipClassName)}
                    text={text}
                    {...TOOLTIP_EXTRA_PROPS}
                />
            )}
        </div>
    )
}
